import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { buildLanternGroup } from './lantern';
import { isGenerated } from './library';
import type { PlacedItem } from './types';

// Instanced drawing of one asset's placements.
//
// A placed layer renders as one InstancedMesh per source-mesh in the GLB —
// draw calls stay flat however many palms get painted, which is the whole
// reason placed content is set dressing rather than pickable objects (design
// decision: interactivity would force real objects and cap counts in the
// tens). Pack GLBs are meshopt-compressed by the pipeline, hence the decoder.

let sharedLoader: GLTFLoader | null = null;
function loader(): GLTFLoader {
    sharedLoader ??= new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    return sharedLoader;
}

const gltfCache = new Map<string, Promise<THREE.Group>>();

export function loadAsset(url: string): Promise<THREE.Group> {
    let cached = gltfCache.get(url);
    if (!cached) {
        // Generated assets never touch the network; the id arrives here in
        // place of a URL.
        cached = isGenerated(url)
            ? Promise.resolve(buildGenerated(url))
            : loader()
                  .loadAsync(url)
                  .then((gltf) => gltf.scene);
        gltfCache.set(url, cached);
    }
    return cached;
}

function buildGenerated(id: string): THREE.Group {
    switch (id) {
        case 'generated/lantern':
            return buildLanternGroup();
        default:
            console.warn('[placement] unknown generated asset', id);
            return new THREE.Group();
    }
}

interface Part {
    geometry: THREE.BufferGeometry;
    material: THREE.Material | THREE.Material[];
    /** Mesh transform relative to the asset root, baked into every instance. */
    local: THREE.Matrix4;
}

/** Native-space measurements of a loaded asset. */
export interface AssetMetrics {
    /** Bounding-box height in the asset's own units. */
    height: number;
    /** Recentre-and-rebase: origin to footprint centre, base to y=0. */
    rebase: THREE.Matrix4;
}

export function measureAsset(root: THREE.Object3D): AssetMetrics {
    const box = new THREE.Box3().setFromObject(root);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    return {
        height: Math.max(size.y, 1e-6),
        rebase: new THREE.Matrix4().makeTranslation(-center.x, -box.min.y, -center.z),
    };
}

export class InstancedLayer {
    readonly group = new THREE.Group();
    /** Native measurements, for scale maths in the editor. */
    metrics!: AssetMetrics;
    private parts: Part[] = [];
    private meshes: THREE.InstancedMesh[] = [];
    private capacity = 0;
    private count = 0;
    private readonly unitsPerMeter: number;

    private constructor(unitsPerMeter: number) {
        this.unitsPerMeter = unitsPerMeter;
    }

    static async create(url: string, unitsPerMeter: number): Promise<InstancedLayer> {
        const layer = new InstancedLayer(unitsPerMeter);
        const source = await loadAsset(url);
        source.updateMatrixWorld(true);
        // Pack assets agree on nothing: some are authored in metres, some in
        // centimetres, some in arbitrary sculpting units (a cm-scale chair
        // read as metres is a hundred metres tall). Nothing here trusts the
        // asset's own units — the layer measures the native bounds once, the
        // editor derives each item's scale from a target size in metres, and
        // the rebase puts every asset's origin at the centre of its base
        // whatever the artist did.
        layer.metrics = measureAsset(source);
        source.traverse((child) => {
            const mesh = child as THREE.Mesh;
            if (!mesh.isMesh) return;
            layer.parts.push({
                geometry: mesh.geometry,
                material: mesh.material,
                local: mesh.matrixWorld.clone(),
            });
        });
        return layer;
    }

    /** Rebuild instance buffers from the item list. */
    setItems(items: PlacedItem[]): void {
        // Grow-only capacity in powers of two: painting adds items every few
        // frames, and reallocating GPU buffers per brush stamp would stutter.
        if (items.length > this.capacity) {
            this.capacity = Math.max(64, 2 ** Math.ceil(Math.log2(items.length)));
            for (const mesh of this.meshes) {
                this.group.remove(mesh);
                mesh.dispose();
            }
            this.meshes = this.parts.map((part) => {
                const mesh = new THREE.InstancedMesh(part.geometry, part.material, this.capacity);
                mesh.frustumCulled = false;
                mesh.castShadow = false;
                mesh.receiveShadow = false;
                this.group.add(mesh);
                return mesh;
            });
        }

        const upm = this.unitsPerMeter;
        const trs = new THREE.Matrix4();
        const composed = new THREE.Matrix4();
        const position = new THREE.Vector3();
        const rotation = new THREE.Quaternion();
        const scale = new THREE.Vector3();
        const up = new THREE.Vector3(0, 1, 0);

        for (let i = 0; i < items.length; i++) {
            const [x, y, z, rotY, s] = items[i];
            position.set(x * upm, y * upm, z * upm);
            rotation.setFromAxisAngle(up, rotY);
            // The stored scale is native-units-to-world including the metre
            // conversion; the editor computed it from a target size in metres
            // against the measured native height, so the layout stays a plain
            // list of numbers the runtime applies without re-measuring.
            scale.setScalar(s * upm);
            trs.compose(position, rotation, scale);
            for (let p = 0; p < this.parts.length; p++) {
                composed
                    .multiplyMatrices(trs, this.metrics.rebase)
                    .multiply(this.parts[p].local);
                this.meshes[p].setMatrixAt(i, composed);
            }
        }
        this.count = items.length;
        for (const mesh of this.meshes) {
            mesh.count = this.count;
            mesh.instanceMatrix.needsUpdate = true;
        }
    }

    /** World-space position of every instance, for systems that track them. */
    instanceWorldPositions(items: PlacedItem[], out: THREE.Vector3[] = []): THREE.Vector3[] {
        out.length = 0;
        const upm = this.unitsPerMeter;
        this.group.updateMatrixWorld(true);
        for (const [x, y, z] of items) {
            out.push(
                new THREE.Vector3(x * upm, y * upm, z * upm).applyMatrix4(this.group.matrixWorld),
            );
        }
        return out;
    }

    dispose(): void {
        for (const mesh of this.meshes) mesh.dispose();
        this.group.removeFromParent();
    }
}
